/**
 * Where the reseller is, as a download surface.
 *
 * Statsboard needs every download attributed to the place it was triggered from, and that is
 * only knowable in the browser at the moment of the click. This is the single place that
 * answers it, so a new download control gets attribution by calling one function rather than
 * by someone remembering to thread a string.
 *
 * ⚠️ Read from the GRID'S OWN STATE, not from the URL. `loaderState.filters` is what the
 * server was asked for, so it already distinguishes a collection from an artist page from a
 * search — including the cases a path cannot, like a search run from the front page. The
 * keys below are the same ones `search/load/filterBuilder.ts` reads. The one exception is
 * Bestsellers, which has no filter of its own and is told apart by the loader's `url`.
 */
import { getLoader } from "../state/loaderState";
import type { DownloadSource } from "@brave-new-world/shared";

export interface DownloadSurface {
  source: DownloadSource;
  /**
   * Omitted rather than null when the surface points at nothing.
   *
   * This object is spread straight into the tRPC input, and `exactOptionalPropertyTypes`
   * makes "absent" and "present but null" different types at that boundary. Omitting is also
   * what the wire wants: a key that is not there is not sent.
   */
  sourceRef?: string;
}

/** A filter value as a non-empty string, or null. */
function str(value: string | number | undefined): string | null {
  if (value === undefined) return null;
  const s = String(value).trim();
  return s === "" || s === "0" ? null : s;
}

/**
 * The Bestsellers page's loader path, as `views/pages/Bestsellers.tsx` seeds it.
 *
 * ⚠️ Matched on the loader's own `url`, not on `location.pathname`. The loader path is what
 * the page declared itself to be, and it survives the `pushState` the grid does while
 * paging — whereas the address bar does not necessarily still say `/bestsellers` by the
 * time someone presses Download.
 */
const BESTSELLERS_PATH = "/bestsellers";

/**
 * The surface the current listing represents.
 *
 * ⚠️ Search outranks the container it was run in. Searching "forest" inside a collection is
 * attributed to the search, because the search is the act that surfaced the artwork — the
 * collection merely bounded it. Crediting the collection would overstate curation, which is
 * the specific thing the attribution exists to measure honestly.
 *
 * Everything that is not one of the named surfaces is `browse`. That is a real answer, not
 * a fallback: the main grid is where most downloads come from.
 */
export function currentDownloadSurface(): DownloadSurface {
  const loader = getLoader();
  const filters = loader?.filters;
  if (!filters) {
    // ⚠️ `unattributed`, not `unknown`. This is a wired download control that asked for its
    // surface and got nothing — a page with no grid state. That is a bug to go and fix, and
    // reporting it as `unknown` would bury it in the legacy bucket nobody chases. See the
    // two values in `shared/downloadSource.ts`.
    return { source: "unattributed" };
  }

  const q = str(filters.q);
  if (q !== null) return { source: "search", sourceRef: q };

  const list = str(filters.lightbox_id);
  if (list !== null) return { source: "list", sourceRef: list };

  const collection = str(filters.collection_id);
  if (collection !== null) return { source: "collection", sourceRef: collection };

  const artist = str(filters.artist_id);
  if (artist !== null) return { source: "artist_profile", sourceRef: artist };

  // Below the four named containers, above `browse`: Bestsellers is a plain grid with no
  // filter of its own to recognise, so only the loader path tells it apart. A search run
  // from it still reports `search`, by the rule above.
  if (loader?.url === BESTSELLERS_PATH) return { source: "bestsellers" };

  return { source: "browse" };
}

/**
 * The same, as query parameters for the plain-GET download route.
 *
 * `/ptdlfile` is a navigation, not a fetch, so its surface has to travel in the URL.
 */
export function downloadSurfaceQuery(): string {
  const { source, sourceRef } = currentDownloadSurface();
  const ref =
    sourceRef === undefined || sourceRef === "" ? "" : `&sref=${encodeURIComponent(sourceRef)}`;
  return `&src=${encodeURIComponent(source)}${ref}`;
}
